/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useCallback, useEffect, useReducer, useState } from 'react';
import { produce } from 'immer';
import { getErrorFromEndpoint, getNestedVal, isObjEmptyOrNull, isStrEmptyOrNull } from '../UtilityFunctions';
import axiosCall from '../../services/axios';
import { useAuthContext } from '../../contexts/AuthContext';
import useAbortController from './useAbortController';
import { useLoanContext } from '../../contexts/LoanContext';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';

type TypeState = {
  prev: any;
  curr: any;
  changesSet: Set<string>;
};

type Action = {
  var?: string;
  val?: any;
  type?: HandleChangeType;
  clearchanges?: boolean;
};

export enum Status {
  LOADING = 'Loading',
  READY = 'Ready',
}

export type HandleChangeType = 'edit' | 'clear' | 'save' | 'cancel';

type Requests = 'GET' | 'POST';

type Endpoint = {
  requestTypes: Requests[];
  path: string;
};

export interface BatchEndpoint extends Endpoint {
  endpointName: string;
}

type BatchEditableArgs = {
  initialObj?: any;
  endpoint?: Endpoint;
  batchEndpoints?: BatchEndpoint[];
};

function getNewState(state: TypeState, variable, val): TypeState {
  const keyArray = variable.split('.'); // determine if nested key value pair, such as dates.DDRDate
  return produce(state, (draft) => {
    var nestedKey = keyArray.slice(0, keyArray.length - 1).reduce((a, k) => (k in a ? a[k] : (a[k] = {})), draft);
    nestedKey[keyArray[keyArray.length - 1]] = val;
  });
}

function getClearedState(state: TypeState) {
  const prevCurrState: TypeState = {
    prev: JSON.parse(JSON.stringify(state.prev)),
    curr: JSON.parse(JSON.stringify(state.prev)),
    changesSet: new Set(),
  };
  return prevCurrState;
}

function isNewValDifferent(state: TypeState, variable: string, newVal) {
  var prevVal = getNestedVal(state.prev, variable);
  if (prevVal === newVal) {
    return false;
  }
  return true;
}

function getEditNewState(state: TypeState, action: Action) {
  var changesSet = new Set(state.changesSet);
  if (isNewValDifferent(state, action.var, action.val)) {
    changesSet.add(action.var);
  } else {
    changesSet.delete(action.var);
  }
  var newState = getNewState(state, 'changesSet', changesSet);
  return getNewState(newState, 'curr.' + action.var, action.val);
}

function getCancelNewState(state: TypeState, action: Action) {
  if (isStrEmptyOrNull(action.var)) {
    var changesSet = new Set<string>();
    var newState = getNewState(state, 'changesSet', changesSet);
    return getNewState(newState, 'curr', getNestedVal(state, 'prev'));
  }
  var changesSet = new Set(state.changesSet);
  changesSet.delete(action.var);
  var newState = getNewState(state, 'changesSet', changesSet);
  return getNewState(newState, 'curr.' + action.var, getNestedVal(state, 'prev.' + action.var));
}

function getSaveNewState(state: TypeState, action: Action) {
  if (isStrEmptyOrNull(action.var)) {
    var changesSet = new Set<string>();
    var newState = getNewState(state, 'changesSet', changesSet);
    newState = getNewState(newState, 'curr', action.val);
    return getNewState(newState, 'prev', action.val);
  } else {
    var changesSet = new Set(state.changesSet);
    changesSet.delete(action.var);
    if (action.clearchanges) {
      changesSet = new Set<string>();
    }
    var newState = getNewState(state, 'changesSet', changesSet);
    if (action.val !== null) {
      newState = getNewState(newState, 'curr.' + action.var, action.val);
      return getNewState(newState, 'prev.' + action.var, action.val);
    } else {
      return getNewState(newState, 'prev.' + action.var, getNestedVal(state, 'curr.' + action.var));
    }
  }
}

function batchEditableReducer(state: TypeState, action: Action): TypeState {
  switch (action.type) {
    case 'edit': {
      return getEditNewState(state, action);
    }
    case 'cancel': {
      return getCancelNewState(state, action);
    }
    case 'save': {
      return getSaveNewState(state, action);
    }
    case 'clear': {
      return getClearedState(state);
    }
  }
}

export function useBatchEditable<T>(args: BatchEditableArgs) {
  const prevCurrState: TypeState = {
    prev: args.initialObj ? args.initialObj : {},
    curr: args.initialObj ? args.initialObj : {},
    changesSet: new Set(),
  };

  const { token } = useAuthContext();
  const { reset } = useAbortController();
  const { enqueueSnackbar } = useSnackbar();

  const [obj, dispatchChange] = useReducer(batchEditableReducer, prevCurrState);
  const hasChanges = obj.changesSet.size != 0;
  const [status, setStatus] = useState<Status>(Status.READY);
  const edit = useCallback((variable: string, newVal) => {
    dispatchChange({
      type: 'edit',
      var: variable,
      val: newVal,
    });
  }, []);

  const cancel = useCallback((variable: string) => {
    dispatchChange({
      type: 'cancel',
      var: variable,
    });
  }, []);

  const save = useCallback((variable: string, val = null, clear = false) => {
    dispatchChange({
      type: 'save',
      var: variable,
      val: val,
      clearchanges: clear,
    });
  }, []);

  const clear = useCallback(() => {
    dispatchChange({
      type: 'clear',
    });
  }, []);

  const handleChange = useCallback(
    (type: HandleChangeType, variable?: string, val = null, clearchanges = false) => {
      if (type === 'edit') {
        edit(variable, val);
      } else if (type === 'save') {
        save(variable, val, clearchanges);
      } else if (type === 'cancel') {
        cancel(variable);
      } else if (type === 'clear') {
        clear();
      }
    },
    [cancel, edit, save, clear],
  );

  const isChanged = useCallback(
    (variable: string) => {
      var prevVal = getNestedVal(obj.prev, variable);
      var currVal = getNestedVal(obj.curr, variable);
      if (prevVal === currVal) {
        return false;
      }
      return true;
    },
    [obj],
  );

  const getInitialData = useCallback(async (): Promise<T> => {
    try {
      if (isObjEmptyOrNull(args.endpoint) || !token || !args.endpoint.requestTypes.includes('GET')) return;

      const request = {
        url: args.endpoint.path,
        method: 'get',
      };
      const config = reset();
      const headers = {
        'Session-Token': token,
      };
      const response = await axiosCall(request, config, headers);
      if (isObjEmptyOrNull(response)) {
        enqueueSnackbar('Unable to retrieve data', { variant: 'error' });
      } else {
        const error = getErrorFromEndpoint(response);
        if (!isStrEmptyOrNull(error)) {
          enqueueSnackbar(error, { variant: 'error' });
        } else {
          handleChange('save', '', response as T);
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const err = getErrorFromEndpoint(error?.response?.data);
        if (!isStrEmptyOrNull(err)) {
          enqueueSnackbar(err, { variant: 'error' });
        }
      } else {
        enqueueSnackbar('Unable to retrieve data ' + error.message, { variant: 'error' });
      }
    } finally {
      setStatus(Status.READY);
    }
  }, [args.endpoint, token, reset, handleChange]);

  const getInitialBatchData = useCallback(async () => {
    if (!token) return;
    if (isObjEmptyOrNull(args?.batchEndpoints)) return;
    const reqbody = {};
    args.batchEndpoints
      .filter((endpoint) => endpoint.requestTypes.includes('GET'))
      .forEach((endpoint) => {
        reqbody[endpoint.endpointName] = {
          endpoint: endpoint.path,
        };
      });
    if (isObjEmptyOrNull(reqbody)) return;
    let request = { url: 'batch', data: reqbody, method: 'post' };
    const headers = {
      'Session-Token': token,
      'Content-Type': 'application/json',
    };
    const config = reset();
    await axiosCall(request, config, headers)
      .then((res) => {
        if (isObjEmptyOrNull(res)) throw new Error('Unable to retrieve data');

        args.batchEndpoints
          .filter((endpoint) => endpoint.requestTypes.includes('GET'))
          .forEach((endpoint) => {
            if (!(endpoint.endpointName in res)) {
              enqueueSnackbar(endpoint.endpointName + " wasn't able to be retrieved.", { variant: 'error' });
            } else {
              const error = getErrorFromEndpoint(res, endpoint.endpointName);
              if (!isStrEmptyOrNull(error)) {
                enqueueSnackbar(error, { variant: 'error' });
              } else {
                handleChange('save', endpoint.endpointName, res[endpoint.endpointName]?.data, true);
              }
            }
          });
      })
      .catch((e) => {
        enqueueSnackbar(e.message, { variant: 'error' });
      });
  }, [reset, handleChange, token, args?.batchEndpoints]);

  const getDataObjForBatchEndpoint = useCallback(
    (endpointName: string) => {
      const data = {};
      obj.changesSet.forEach((changedVar) => {
        const endpointNameChanged = changedVar.split('.')[0];
        const endpointVarChanged = changedVar.split('.')[1];
        if (endpointNameChanged == endpointName) {
          data[endpointVarChanged] = getNestedVal(obj.curr, changedVar);
        }
      });
      return data;
    },
    [JSON.stringify(obj)],
  );

  const saveBatchEndpointChangesToBackend = useCallback(async () => {
    try {
      if (!token) return;
      if (isObjEmptyOrNull(args?.batchEndpoints)) return;
      setStatus(Status.LOADING);
      const reqbody: { [key: string]: { endpoint: string; data: any } } = {};
      const endpointsChanged = new Set<string>();
      args?.batchEndpoints
        .filter((endpoint) => endpoint.requestTypes.includes('POST'))
        .forEach((endpoint) => {
          const data = getDataObjForBatchEndpoint(endpoint.endpointName);
          if (!isObjEmptyOrNull(data)) {
            reqbody[endpoint.endpointName] = {
              endpoint: endpoint.path,
              data: getDataObjForBatchEndpoint(endpoint.endpointName),
            };
            endpointsChanged.add(endpoint.endpointName);
          }
        });
      if (endpointsChanged.size == 0) return;
      let request = { url: 'batch', data: reqbody, method: 'post' };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const config = reset();
      await axiosCall(request, config, headers)
        .then((res) => {
          if (isObjEmptyOrNull(res)) throw new Error('Unable to retrieve data');
          endpointsChanged.forEach((endpoint) => {
            if (!(endpoint in res)) {
              enqueueSnackbar(endpoint + " couldn't be saved", { variant: 'error' });
            } else {
              const error = getErrorFromEndpoint(res, endpoint);
              if (!isStrEmptyOrNull(error)) {
                enqueueSnackbar(error, { variant: 'error' });
              }
            }
          });
        })
        .catch((e) => {
          enqueueSnackbar('Unable to save for ' + endpointsChanged.forEach((endpoint) => endpoint + ','), {
            variant: 'error',
          });
        });
      args?.batchEndpoints
        .filter((endpoint) => !endpoint.requestTypes.includes('GET'))
        .forEach((endpoint) => {
          handleChange('save', endpoint.endpointName, {}, true);
        });
    } catch (e) {
      enqueueSnackbar('Unable to save' + e.message, {
        variant: 'error',
      });
    } finally {
      await getInitialBatchData();
    }
  }, [token, args, handleChange, getInitialBatchData, JSON.stringify(args.batchEndpoints)]);

  const getDataObj = useCallback(() => {
    if (isObjEmptyOrNull(args.endpoint) || !args.endpoint.requestTypes.includes('POST')) return;
    const data = {};
    obj.changesSet.forEach((changedVar) => {
      data[changedVar] = getNestedVal(obj.curr, changedVar);
    });
    return data;
  }, [obj.curr, obj.changesSet, args.endpoint]);

  const saveEndpoint = useCallback(async () => {
    try {
      if (isObjEmptyOrNull(args.endpoint) || !token || !args.endpoint.requestTypes.includes('POST')) return;
      setStatus(Status.LOADING);
      const dataObj = getDataObj();
      const request = {
        url: args.endpoint.path,
        method: 'post',
        data: dataObj,
      };
      if (isObjEmptyOrNull(dataObj)) return;
      const config = reset();
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const response = await axiosCall(request, config, headers);
      if (isObjEmptyOrNull(response)) {
        enqueueSnackbar('Unable to save data', { variant: 'error' });
      } else {
        const error = getErrorFromEndpoint(response);
        if (!isStrEmptyOrNull(error)) {
          enqueueSnackbar(error, { variant: 'error' });
        }
        if (!args.endpoint.requestTypes.includes('GET')) {
          handleChange('save', '', {});
        }
      }
    } catch (error) {
      enqueueSnackbar('Unable to save data ' + error.message, { variant: 'error' });
    } finally {
      await getInitialData();
    }
  }, [JSON.stringify(args.endpoint), enqueueSnackbar, handleChange, reset, getDataObj, getInitialData]);

  const fetch = useCallback(async () => {
    if (!isStrEmptyOrNull(args?.endpoint?.path)) {
      setStatus(Status.LOADING);
      await getInitialData();
    } else if (!isObjEmptyOrNull(args?.batchEndpoints)) {
      setStatus(Status.LOADING);
      await getInitialBatchData();
    }
  }, [args?.endpoint?.path, JSON.stringify(args?.batchEndpoints), getInitialData, getInitialBatchData]);

  useEffect(() => {
    fetch().finally(() => {
      setStatus(Status.READY);
    });
    // token is required: on a page refresh the endpoint path resolves from storage before
    // the session token is restored, and the initial fetch would otherwise be skipped for good.
  }, [args?.endpoint?.path, JSON.stringify(args?.batchEndpoints), token]);

  const refetch = useCallback(() => {
    fetch().finally(() => {
      setStatus(Status.READY);
    });
  }, [fetch]);

  const saveChangesToBackend = useCallback(async () => {
    async function startFetching() {
      if (obj.changesSet.size != 0) {
        await saveEndpoint();
        await saveBatchEndpointChangesToBackend();
      }
    }

    await startFetching().finally(() => {
      setStatus(Status.READY);
    });
  }, [obj.changesSet, saveEndpoint, saveBatchEndpointChangesToBackend]);

  const editableArr = {
    curr: obj.curr as T,
    prev: obj.prev as T,
    handleChange,
    isChanged,
    hasChanges,
    saveChangesToBackend,
    status,
    refetch,
  };

  return editableArr;
}
